import { Button } from "@/components/ui/button";
import { resolveInvoiceCustomerLabel, type InvoiceWithProject } from "@/hooks/use-invoices";
import { formatDate, formatNOK } from "@/lib/format";
import { cn } from "@/lib/utils";
import { TONE_TEXT } from "@/lib/widget-status";
import { daysOverdue, invoiceDisplayStatus } from "@/lib/invoice-list";
import { InvoiceStatusPill } from "./InvoiceStatusPill";
import { InvoiceSendError } from "./InvoiceSendError";
import { InvoiceTypeCell } from "./InvoiceTypeCell";

interface InvoiceMobileListProps {
  rows: InvoiceWithProject[];
  totalCount: number;
  today: string;
  /** Fakturaer som er gjort opp av kreditnota. */
  credited: ReadonlySet<string>;
  invoiceNoById: Map<string, number | null>;
  onOpenPdf: (invoiceId: string) => void;
  onShowMore: () => void;
  emptyText: string;
}

/** Fakturaer som kort under sm-bredde, med «Vis flere» i stedet for sidetall (VIB-2948). */
export function InvoiceMobileList({
  rows,
  totalCount,
  today,
  credited,
  invoiceNoById,
  onOpenPdf,
  onShowMore,
  emptyText,
}: InvoiceMobileListProps) {
  if (rows.length === 0) return <p className="py-8 text-center text-muted-foreground">{emptyText}</p>;

  return (
    <div className="grid gap-3">
      {rows.map((inv) => {
        const status = invoiceDisplayStatus(inv, today, credited);
        const hasPdf = !!inv.poweroffice_invoice_id;
        return (
          <div
            key={inv.id}
            role={hasPdf ? "button" : undefined}
            tabIndex={hasPdf ? 0 : undefined}
            aria-label={hasPdf ? `Åpne PDF for faktura ${inv.invoice_no ?? inv.id}` : undefined}
            onClick={hasPdf ? () => onOpenPdf(inv.id) : undefined}
            onKeyDown={hasPdf ? (e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onOpenPdf(inv.id)) : undefined}
            className={cn("min-w-0 border-[1.5px] border-border rounded-lg p-3 grid gap-1.5", hasPdf && "cursor-pointer hover:bg-muted/40")}
          >
            <div className="flex justify-between items-baseline gap-2">
              <span className="min-w-0 truncate">
                {inv.project?.project_code && <b className="font-semibold">{inv.project.project_code} · </b>}
                {resolveInvoiceCustomerLabel(inv)}
              </span>
              <InvoiceStatusPill status={status} daysOverdue={daysOverdue(inv, today)} paidDate={inv.paid_date} />
            </div>
            <div className="flex justify-between items-baseline gap-2">
              <span className="min-w-0 text-sm text-muted-foreground">
                <InvoiceTypeCell invoice={inv} invoiceNoById={invoiceNoById} />
                {inv.invoice_no != null && ` · nr ${inv.invoice_no}`}
              </span>
              <span className={cn("font-display font-semibold text-lg tabular-nums whitespace-nowrap", inv.amount < 0 && TONE_TEXT.miss)}>
                {formatNOK(inv.amount)}
              </span>
            </div>
            <div className="flex justify-between gap-2 text-xs text-muted-foreground">
              <span>{formatDate(inv.invoice_date)}</span>
              {inv.due_date && status !== "credit_note" && (
                <span className={cn(status === "overdue" && TONE_TEXT.miss)}>Forfall {formatDate(inv.due_date)}</span>
              )}
            </div>
            {status === "draft" && inv.last_send_error && <InvoiceSendError error={inv.last_send_error} />}
          </div>
        );
      })}
      <div className="flex justify-between items-center text-sm text-muted-foreground">
        <span>1–{rows.length} av {totalCount}</span>
        {rows.length < totalCount && (
          <Button variant="outline" size="sm" onClick={onShowMore}>Vis flere</Button>
        )}
      </div>
    </div>
  );
}
